<template>
  <a-drawer
    :open="open"
    :width="1080"
    placement="right"
    title="组件审核工作台"
    @close="emit('update:open', false)"
  >
    <template v-if="review">
      <header class="review-head">
        <div>
          <h2>{{ review.package?.name ?? review.packageId }}</h2>
          <p>固定版本 v{{ review.release?.version ?? '-' }} · {{ review.releaseId }}</p>
        </div>
        <a-button type="primary" :loading="assessing" @click="runAssessment">
          <i class="ri-shield-check-line" />执行 / 重跑 Agent 初审
        </a-button>
      </header>

      <a-alert
        type="warning"
        show-icon
        message="Agent 负责初审分流和证据归纳，不会自动上架或驳回；最终审核仍由管理员完成。审核试用的网络仅允许 Manifest 声明域名内的真实 GET/HEAD 请求。"
      />

      <a-tabs class="review-tabs">
        <a-tab-pane key="overview" tab="发布与权限">
          <section class="facts">
            <div>
              <span>产物摘要</span
              ><code>{{ artifact?.sourceHash ?? review.release?.artifactHash ?? '-' }}</code>
            </div>
            <div>
              <span>提交时间</span><strong>{{ date(review.submittedAt) }}</strong>
            </div>
            <div>
              <span>更新说明</span><strong>{{ review.release?.changelog || '未填写' }}</strong>
            </div>
          </section>
          <section class="section">
            <h3>声明能力</h3>
            <a-empty
              v-if="!capabilities.length"
              :image="false"
              description="此组件未声明运行时能力"
            />
            <a-space v-else wrap
              ><a-tag v-for="item in capabilities" :key="item" color="blue">{{
                item
              }}</a-tag></a-space
            >
          </section>
          <section class="section">
            <h3>网络与依赖</h3>
            <p>允许域名：{{ domains.length ? domains.join('、') : '未声明' }}</p>
            <p>冻结依赖：{{ dependencyNames.length ? dependencyNames.join('、') : '无' }}</p>
          </section>
        </a-tab-pane>

        <a-tab-pane key="preview" tab="隔离试用">
          <section class="preview-copy">
            <h3>冻结版本预览</h3>
            <p>
              在独立、不透明来源的 iframe 中运行。数据和文件写入本审核会话；网络仅可真实读取
              Manifest 已声明域名，连接器、命令与通知不会触达外部系统。
            </p>
          </section>
          <a-button
            v-if="!descriptor"
            :loading="loadingArtifact"
            type="primary"
            @click="loadArtifact"
          >
            <i class="ri-play-line" />加载审核预览
          </a-button>
          <template v-if="descriptor && reviewSession">
            <section class="review-runtime-controls">
              <div>
                <strong>本次授予能力</strong>
                <p>仅能授予组件已在 Manifest 中声明的能力；取消后会立即重建 iframe。</p>
              </div>
              <a-checkbox-group
                v-model:value="selectedGrants"
                :options="capabilityOptions"
                @change="saveGrants"
              />
              <a-space>
                <a-button size="small" @click="resetReviewSession"
                  ><i class="ri-refresh-line" />重置隔离数据</a-button
                >
                <a-tag color="blue">{{ reviewSession.calls.length }} 次运行时调用</a-tag>
              </a-space>
            </section>
          </template>
          <a-alert
            v-if="previewError"
            type="error"
            show-icon
            :message="previewError"
            class="inline-error"
          />
          <div v-if="descriptor" class="preview-frame-shell">
            <iframe
              ref="previewFrame"
              :key="previewRevision"
              sandbox="allow-scripts" :allow="componentFramePolicy(descriptor?.context.grants)"
              :srcdoc="previewDocument"
              title="组件审核隔离预览"
            />
          </div>
          <a-collapse v-if="descriptor" class="source-collapse">
            <a-collapse-panel key="source" header="查看冻结源码">
              <pre>{{ descriptor.source }}</pre>
            </a-collapse-panel>
          </a-collapse>
        </a-tab-pane>

        <a-tab-pane key="assessment" tab="Agent 初审">
          <a-empty
            v-if="!assessment"
            description="初审尚未完成。新提交会自动触发；也可在此重跑初审。"
          />
          <template v-else>
            <section class="agent-summary">
              <h3>Agent 初审</h3>
              <a-tag :color="agentColor">{{ agentStatus }}</a-tag>
              <p>{{ assessment.agent?.summary ?? '未生成 Agent 报告。' }}</p>
              <p v-if="assessment.agent"><strong>初审结论：</strong>{{ initialDecision }}</p>
              <ul v-if="assessment.agent?.findings.length">
                <li v-for="item in assessment.agent.findings" :key="item">{{ item }}</li>
              </ul>
            </section>
            <section class="section">
              <h3>可核验检查项</h3>
              <a-list :data-source="assessment.staticFindings" size="small" bordered>
                <template #renderItem="{ item }"
                  ><a-list-item
                    ><a-tag :color="findingColor(item.level)">{{ findingLabel(item.level) }}</a-tag
                    >{{ item.message }}</a-list-item
                  ></template
                >
              </a-list>
            </section>
          </template>
        </a-tab-pane>

        <a-tab-pane key="tests" tab="审核测试记录">
          <a-form :model="testDraft" layout="vertical" @finish="saveTest">
            <div class="test-form">
              <a-form-item
                label="测试场景"
                name="scenario"
                :rules="[{ required: true, message: '请填写测试场景' }]"
              >
                <a-input
                  v-model:value="testDraft.scenario"
                  :maxlength="160"
                  placeholder="例如：首次加载与主要按钮交互"
                />
              </a-form-item>
              <a-form-item label="结果" name="result"
                ><a-select v-model:value="testDraft.result" :options="testResultOptions"
              /></a-form-item>
            </div>
            <a-form-item
              label="证据"
              name="evidence"
              :rules="[{ required: true, message: '请记录观察到的结果或错误' }]"
            >
              <a-textarea
                v-model:value="testDraft.evidence"
                :maxlength="1000"
                :rows="3"
                show-count
                placeholder="记录验证步骤、预览行为、控制台错误或阻断原因"
              />
            </a-form-item>
            <a-button html-type="submit" type="primary" :loading="savingTest"
              ><i class="ri-add-line" />记录测试结果</a-button
            >
          </a-form>
          <a-divider />
          <a-empty v-if="!testRecords.length" :image="false" description="还没有人工测试记录" />
          <a-list v-else :data-source="testRecords" bordered size="small">
            <template #renderItem="{ item }"
              ><a-list-item
                ><div class="test-record">
                  <div>
                    <strong>{{ item.scenario }}</strong
                    ><a-tag :color="testResultColor(item.result)">{{
                      testResultLabel(item.result)
                    }}</a-tag>
                  </div>
                  <p>{{ item.evidence }}</p>
                  <small>{{ date(item.createdAt) }} · {{ item.reviewerId }}</small>
                </div></a-list-item
              ></template
            >
          </a-list>
        </a-tab-pane>
      </a-tabs>
    </template>
  </a-drawer>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { createComponentRuntimeRequest, createComponentFileReferenceApi, createComponentFunctionApi, createComponentMediaApi, createComponentDeviceApi, createComponentLinkApi, componentFramePolicy } from 'shared';
import { useComponentDataFeedback } from '../../../frontend/src/composables/useComponentDataFeedback';
import { deviceError } from '../../../frontend/src/services/component-device';
import type {
  CapabilityName,
  ComponentRuntimeDescriptor,
  MarketplaceReviewArtifactView,
  MarketplaceReviewAssessment,
  MarketplaceReviewSessionView,
  MarketplaceReviewTestRecord,
  MarketplaceReviewView,
} from 'shared';
import { adminApi } from '@/api';

const props = defineProps<{ open: boolean; review?: MarketplaceReviewView }>();
const emit = defineEmits<{ 'update:open': [value: boolean]; changed: [] }>();
const artifact = ref<MarketplaceReviewArtifactView>();
const descriptor = ref<ComponentRuntimeDescriptor>();
const runtimeToken = ref('');
let runtimeVersion = 0;
const dataFeedback = useComponentDataFeedback();
const reviewSession = ref<MarketplaceReviewSessionView>();
const selectedGrants = ref<CapabilityName[]>([]);
const assessment = ref<MarketplaceReviewAssessment | null>();
const loadingArtifact = ref(false);
const assessing = ref(false);
const savingTest = ref(false);
const previewError = ref('');
const previewFrame = ref<HTMLIFrameElement>();
const previewRevision = ref(0);
const testRecords = ref<MarketplaceReviewTestRecord[]>([]);
const testDraft = ref<{
  scenario: string;
  result: MarketplaceReviewTestRecord['result'];
  evidence: string;
}>({ scenario: '', result: 'passed', evidence: '' });
const testResultOptions = [
  { value: 'passed', label: '通过' },
  { value: 'failed', label: '失败' },
  { value: 'blocked', label: '阻断' },
];
const capabilities = computed(() =>
  props.review?.release?.manifest.type === 'component'
    ? props.review.release.manifest.requestedCapabilities
    : [],
);
const domains = computed(() =>
  props.review?.release?.manifest.type === 'component'
    ? (props.review.release.manifest.componentManifest?.network?.allowedDomains ?? [])
    : [],
);
const dependencyNames = computed(
  () => props.review?.release?.dependencies.map((item) => item.packageId) ?? [],
);
const agentStatus = computed(
  () =>
    ({ ready: '初审完成', unavailable: '未配置模型', failed: '初审失败' })[
      assessment.value?.agent?.status ?? 'unavailable'
    ],
);
const agentColor = computed(
  () =>
    ({ ready: 'blue', unavailable: 'default', failed: 'red' })[
      assessment.value?.agent?.status ?? 'unavailable'
    ],
);
const initialDecision = computed(
  () =>
    ({
      pass: '通过初审，进入人工终审',
      block: '初审拦截，须优先核验问题',
      manual_review: '初审无法判断，转人工复核',
    })[assessment.value?.agent?.initialDecision ?? 'manual_review'],
);
const capabilityOptions = computed(
  () => reviewSession.value?.requestedCapabilities.map((value) => ({ label: value, value })) ?? [],
);
const previewDocument = computed(() => (descriptor.value ? reviewDocument(descriptor.value) : ''));

function date(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-';
}
function findingColor(level: string): string {
  return (
    ({ info: 'blue', warning: 'orange', high: 'red' } as Record<string, string>)[level] ?? 'default'
  );
}
function findingLabel(level: string): string {
  return (
    ({ info: '提示', warning: '注意', high: '高风险' } as Record<string, string>)[level] ?? level
  );
}
function testResultColor(result: string): string {
  return (
    ({ passed: 'green', failed: 'red', blocked: 'orange' } as Record<string, string>)[result] ??
    'default'
  );
}
function testResultLabel(result: string): string {
  return (
    ({ passed: '通过', failed: '失败', blocked: '阻断' } as Record<string, string>)[result] ??
    result
  );
}

async function loadArtifact(): Promise<void> {
  if (!props.review) return;
  dataFeedback.reset();
  const version = ++runtimeVersion;
  const reviewId = props.review.id;
  runtimeToken.value = '';
  descriptor.value = undefined;
  loadingArtifact.value = true;
  previewError.value = '';
  try {
    const [nextSession, nextArtifact] = await Promise.all([
      adminApi.post<MarketplaceReviewSessionView>(
        `/admin/marketplace/reviews/${reviewId}/sessions`,
        {},
      ),
      adminApi.get<MarketplaceReviewArtifactView>(
        `/admin/marketplace/reviews/${reviewId}/artifact`,
      ),
    ]);
    const nextDescriptor = await adminApi.get<ComponentRuntimeDescriptor>(
      `/admin/marketplace/review-sessions/${nextSession.id}/runtime`,
    );
    if (version !== runtimeVersion) return;
    reviewSession.value = nextSession;
    selectedGrants.value = [...nextSession.grants];
    artifact.value = nextArtifact;
    descriptor.value = nextDescriptor;
    runtimeToken.value = nextDescriptor.token;
    previewRevision.value += 1;
  } catch (error) {
    if (version === runtimeVersion)
      previewError.value = error instanceof Error ? error.message : '加载冻结产物失败';
  } finally {
    if (version === runtimeVersion) loadingArtifact.value = false;
  }
}
async function saveGrants(): Promise<void> {
  if (!reviewSession.value) return;
  dataFeedback.reset();
  const version = ++runtimeVersion;
  const sessionId = reviewSession.value.id;
  runtimeToken.value = '';
  try {
    const nextSession = await adminApi.patch<MarketplaceReviewSessionView>(
      `/admin/marketplace/review-sessions/${sessionId}/grants`,
      { grants: selectedGrants.value },
    );
    const nextDescriptor = await adminApi.get<ComponentRuntimeDescriptor>(
      `/admin/marketplace/review-sessions/${sessionId}/runtime`,
    );
    if (version !== runtimeVersion) return;
    reviewSession.value = nextSession;
    selectedGrants.value = [...nextSession.grants];
    descriptor.value = nextDescriptor;
    runtimeToken.value = nextDescriptor.token;
    previewRevision.value += 1;
  } catch (error) {
    if (version === runtimeVersion)
      message.error(error instanceof Error ? error.message : '更新试用权限失败');
  }
}
async function resetReviewSession(): Promise<void> {
  if (!reviewSession.value) return;
  dataFeedback.reset();
  const version = ++runtimeVersion;
  const sessionId = reviewSession.value.id;
  runtimeToken.value = '';
  try {
    const nextSession = await adminApi.post<MarketplaceReviewSessionView>(
      `/admin/marketplace/review-sessions/${sessionId}/reset`,
      {},
    );
    const nextDescriptor = await adminApi.get<ComponentRuntimeDescriptor>(
      `/admin/marketplace/review-sessions/${sessionId}/runtime`,
    );
    if (version !== runtimeVersion) return;
    reviewSession.value = nextSession;
    descriptor.value = nextDescriptor;
    runtimeToken.value = nextDescriptor.token;
    previewRevision.value += 1;
    message.success('本审核会话的数据、文件和调用记录已重置');
  } catch (error) {
    if (version === runtimeVersion)
      message.error(error instanceof Error ? error.message : '重置隔离会话失败');
  }
}
const requestReviewRuntime = createComponentRuntimeRequest({
  getToken: () => runtimeToken.value,
  getScope: () => runtimeVersion,
  renewToken: async () => {
    const version = runtimeVersion;
    if (!reviewSession.value) throw new Error('审核试用会话尚未就绪');
    const next = await adminApi.get<ComponentRuntimeDescriptor>(
      `/admin/marketplace/review-sessions/${reviewSession.value.id}/runtime`,
    );
    if (version !== runtimeVersion) throw new Error('审核试用会话已变化，请重新操作');
    runtimeToken.value = next.token;
  },
});
async function loadAssessment(): Promise<void> {
  if (!props.review) return;
  assessment.value = await adminApi.get<MarketplaceReviewAssessment | null>(
    `/admin/marketplace/reviews/${props.review.id}/assessment`,
  );
}
async function runAssessment(): Promise<void> {
  if (!props.review) return;
  assessing.value = true;
  try {
    assessment.value = await adminApi.post<MarketplaceReviewAssessment>(
      `/admin/marketplace/reviews/${props.review.id}/assessment`,
      {},
    );
    message.success('Agent 初审已完成');
    emit('changed');
  } catch (error) {
    message.error(error instanceof Error ? error.message : '检查执行失败');
  } finally {
    assessing.value = false;
  }
}
async function saveTest(): Promise<void> {
  if (!props.review) return;
  savingTest.value = true;
  try {
    const record = await adminApi.post<MarketplaceReviewTestRecord>(
      `/admin/marketplace/reviews/${props.review.id}/test-records`,
      testDraft.value,
    );
    testRecords.value = [record, ...testRecords.value];
    testDraft.value = { scenario: '', result: 'passed', evidence: '' };
    message.success('测试记录已保存');
    emit('changed');
  } catch (error) {
    message.error(error instanceof Error ? error.message : '测试记录保存失败');
  } finally {
    savingTest.value = false;
  }
}
function encode(value: unknown): string {
  return btoa(
    unescape(encodeURIComponent(typeof value === 'string' ? value : JSON.stringify(value))),
  );
}
function reviewDocument(current: ComponentRuntimeDescriptor): string {
  const source = encode(current.source);
  const bootstrap = encode(current.context);
  const closingScript = '</scr' + 'ipt>';
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline' 'unsafe-eval'; style-src 'unsafe-inline'; img-src data: blob:; font-src data:; media-src blob:; connect-src 'none'"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{width:100%;height:100%;min-height:100%;margin:0;padding:0;box-sizing:border-box;overflow:hidden;font:14px system-ui,sans-serif}*,*::before,*::after{box-sizing:inherit}body{background:#fff;color:#111827}${current.tagName}{display:block;width:100%;height:100%;min-height:100%}</style></head><body><${current.tagName}></${current.tagName}><script>
const decode=value=>decodeURIComponent(escape(atob(value)));const bootstrap=JSON.parse(decode('${bootstrap}'));const pending=new Map();let sequence=0;
const call=(method,payload={})=>{const requestId=String(++sequence);parent.postMessage({channel:'marketplace-review-runtime',requestId,method,payload},'*');return new Promise((resolve,reject)=>pending.set(requestId,{resolve,reject}));};
window.addEventListener('message',event=>{if(event.source!==parent)return;const message=event.data;if(message?.channel!=='marketplace-review-runtime-response')return;const request=pending.get(message.requestId);if(!request)return;pending.delete(message.requestId);message.error?request.reject(Object.assign(new Error(typeof message.error==='string'?message.error:message.error.message),typeof message.error==='object'?message.error:{})):request.resolve(message.data);});
const temporaryImages=(${createComponentFileReferenceApi.toString()})(call);
window.ctx=Object.freeze({lock:async()=>{throw new Error('审核预览不支持真实锁定');},identity:{current:async()=>structuredClone(bootstrap.identity),profile:()=>call('identity.profile')},workspace:{current:async()=>structuredClone(bootstrap.workspace)},theme:{current:async()=>structuredClone(bootstrap.workspace.appearance)},instance:{current:async()=>structuredClone(bootstrap.instance)},settings:{current:async()=>structuredClone(bootstrap.settings)},agent:{available:()=>false,open:()=>Promise.reject(new Error('审核会话不提供 Agent'))},capabilities:{has:name=>bootstrap.grants.includes(name),list:()=>[...bootstrap.grants]},permissions:{has:name=>bootstrap.operations.includes(name),list:()=>[...bootstrap.operations]},data:{import:input=>call('data.import',input),query:input=>call('data.query',input),create:input=>call('data.create',input),update:input=>call('data.update',input),delete:input=>call('data.delete',input),subscribe:async(input,handler)=>{if(typeof handler!=='function')throw new Error('数据变更处理器必须是函数');await call('data.subscribe',input);return()=>void call('data.unsubscribe',input).catch(()=>undefined);}},links:(${createComponentLinkApi.toString()})(call),pages:{list:()=>call('page.list'),navigate:pageId=>call('page.navigate',{pageId})},members:{list:()=>call('workspace.members.list')},files:{getRemoteImageUrl:temporaryImages.getRemoteImageUrl,releaseImageUrl:temporaryImages.releaseImageUrl,upload:(file,input={})=>{if(!(file instanceof File))throw new Error('upload 的第一个参数必须是 File');const {onProgress,...options}=input||{};return call('files.upload',{...options,file});},list:()=>call('files.list'),read:fileId=>call('files.read',{fileId}),write:input=>call('files.write',input)},functions:(${createComponentFunctionApi.toString()})(call),media:(${createComponentMediaApi.toString()})(call),device:(${createComponentDeviceApi.toString()})(call,'component-device-event'),network:{request:input=>call('network.request',input)},connector:{invoke:input=>call('connector.invoke',input)},commands:{execute:input=>call('command.execute',input)},notifications:{send:input=>call('notification.send',input)},realtime:{publish:(topic,payload)=>call('realtime.publish',{topic,payload}),subscribe:async(topic,handler)=>{if(typeof handler!=='function')throw new Error('实时订阅处理器必须是函数');await call('realtime.subscribe',{topic});return()=>void call('realtime.unsubscribe',{topic}).catch(()=>undefined);}},events:{emit:(name,payload)=>call('events.emit',{name,payload})},actions:{on:()=>()=>undefined},on:()=>()=>undefined});
window.addEventListener('error',event=>parent.postMessage({channel:'review-preview-error',message:event.message},'*'));window.addEventListener('unhandledrejection',event=>parent.postMessage({channel:'review-preview-error',message:event.reason instanceof Error?event.reason.message:String(event.reason)},'*'));try{eval(decode('${source}'))}catch(error){parent.postMessage({channel:'review-preview-error',message:error instanceof Error?error.message:String(error)},'*')}${closingScript}</body></html>`;
}
async function bridge(event: MessageEvent): Promise<void> {
  if (
    event.source !== previewFrame.value?.contentWindow ||
    event.data?.channel !== 'marketplace-review-runtime' ||
    !reviewSession.value ||
    !descriptor.value
  )
    return;
  const { requestId, method, payload } = event.data as {
    requestId: string;
    method: string;
    payload?: Record<string, unknown>;
  };
  const sessionId = reviewSession.value.id;
  const version = runtimeVersion;
  const target = previewFrame.value?.contentWindow;
  try {
    let data: unknown;
    if (method.startsWith('device.')) throw deviceError('DEVICE_PERMISSION_DENIED', '审核会话禁止真实设备采集');
    if (method === 'files.upload') {
      if (!(payload?.file instanceof File)) throw new Error('请选择要上传的文件');
      if (payload.file.size > 10 * 1024 * 1024) throw new Error('审核上传文件不能超过 10 MiB');
      const file = payload.file;
      const scope = typeof payload.scope === 'string' ? payload.scope : 'instance';
      data = await requestReviewRuntime((token) => {
        const form = new FormData();
        form.append('file', file);
        form.append('token', token);
        form.append('scope', scope);
        return adminApi.post(`/admin/marketplace/review-sessions/${sessionId}/files`, form);
      });
    } else {
      data = await dataFeedback.call(method, payload ?? {}, (input) =>
        requestReviewRuntime(
          (token) =>
            adminApi.post<unknown>(`/admin/marketplace/review-sessions/${sessionId}/ctx`, {
              token,
              method,
              payload: input,
            }),
          version,
        ),
      );
    }
    target?.postMessage({ channel: 'marketplace-review-runtime-response', requestId, data }, '*');
  } catch (error) {
    if (version !== runtimeVersion || target !== previewFrame.value?.contentWindow) return;
    const appError = {
      ...(error && typeof error === 'object' ? error : {}),
      message: error instanceof Error ? error.message : '审核能力调用失败',
    };
    target?.postMessage(
      {
        channel: 'marketplace-review-runtime-response',
        requestId,
        error: appError,
      },
      '*',
    );
  }
}
function previewMessage(event: MessageEvent): void {
  if (
    event.source === previewFrame.value?.contentWindow &&
    event.data?.channel === 'review-preview-error'
  )
    previewError.value = `预览运行错误：${String(event.data.message ?? '未知错误')}`;
}
watch(
  () => [props.open, props.review?.id] as const,
  async ([open, reviewId]) => {
    runtimeVersion += 1;
    runtimeToken.value = '';
    loadingArtifact.value = false;
    if (!open || !reviewId) return;
    artifact.value = undefined;
    descriptor.value = undefined;
    reviewSession.value = undefined;
    selectedGrants.value = [];
    previewError.value = '';
    testRecords.value = props.review?.testRecords ?? [];
    try {
      await loadAssessment();
    } catch {
      assessment.value = props.review?.assessment ?? null;
    }
  },
  { immediate: true },
);
window.addEventListener('message', previewMessage);
window.addEventListener('message', bridge);
onBeforeUnmount(() => {
  runtimeVersion += 1;
  runtimeToken.value = '';
  window.removeEventListener('message', previewMessage);
  window.removeEventListener('message', bridge);
});
</script>

<style scoped>
.review-head {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 20px;
}
.review-head h2 {
  margin: 0;
  font-size: 24px;
}
.review-head p,
.preview-copy p {
  margin: 6px 0 0;
  color: var(--aw-text-secondary);
  font-size: 14px;
}
.review-tabs {
  margin-top: 18px;
}
.facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 4px 0 20px;
}
.facts > div {
  display: grid;
  gap: 6px;
  padding: 12px;
  border: 1px solid var(--aw-border);
  border-radius: 8px;
}
.facts span,
.test-record small {
  color: var(--aw-text-tertiary);
  font-size: 12px;
}
.facts code {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}
.section {
  margin-top: 24px;
}
.section h3,
.preview-copy h3,
.agent-summary h3 {
  margin: 0 0 10px;
  font-size: 16px;
}
.section p {
  margin: 8px 0;
  color: var(--aw-text-secondary);
}
.preview-copy {
  margin-bottom: 16px;
}
.review-runtime-controls {
  display: grid;
  gap: 10px;
  margin: 16px 0;
  padding: 12px;
  border: 1px solid var(--aw-border);
  border-radius: 8px;
  background: var(--aw-surface-subtle);
}
.review-runtime-controls p {
  margin: 4px 0 0;
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.preview-frame-shell {
  height: 480px;
  margin-top: 16px;
  overflow: hidden;
  border: 1px solid var(--aw-border);
  border-radius: 8px;
  background: #fff;
}
.preview-frame-shell iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}
.inline-error {
  margin-top: 14px;
}
.source-collapse {
  margin-top: 16px;
}
.source-collapse pre {
  max-height: 420px;
  margin: 0;
  overflow: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font:
    12px/1.55 ui-monospace,
    SFMono-Regular,
    Consolas,
    monospace;
}
.agent-summary {
  padding: 16px;
  border: 1px solid var(--aw-border);
  border-radius: 8px;
}
.agent-summary h3 {
  display: inline-block;
  margin-right: 8px;
}
.agent-summary p {
  margin: 10px 0;
  color: var(--aw-text-secondary);
}
.agent-summary ul {
  margin: 8px 0 0;
  padding-left: 20px;
}
.test-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px;
  gap: 14px;
}
.test-record {
  width: 100%;
}
.test-record div {
  display: flex;
  gap: 8px;
  align-items: center;
}
.test-record p {
  margin: 7px 0;
  color: var(--aw-text-secondary);
  white-space: pre-wrap;
}
@media (max-width: 720px) {
  .review-head {
    display: grid;
  }
  .facts,
  .test-form {
    grid-template-columns: 1fr;
  }
  .preview-frame-shell {
    height: 360px;
  }
}
</style>
